import type {
  MediaImageUploadUrlResponse,
  MediaVideoUploadUrlResponse,
} from '@coorum-research/shared-common/api-response'
import type {
  CreateUploadImageUrlValues,
  CreateUploadVideoUrlValues,
  UploadImageResponse,
  UploadVideoResponse,
} from '@coorum-research/shared-common/types'
import type { AxiosInstance } from 'axios'
import { cloudflareImageApi } from './cloudflare-image.api'
import {
  getPublicCloudflareImageUrl,
  getPublicCloudflareVideoHlsUrl,
  getPublicCloudflareVideoThumbnailUrl,
} from './cloudflare-image.util'

/**
 * admin-apiとuser-apiが
 * api endpointまで同じなので、共通化する
 */
export function mediaFileApiFactory(http: AxiosInstance) {
  const { uploadImageByUploadUrl, uploadVideoByUploadUrl } = cloudflareImageApi()

  const createUploadImageUrl = async (data: CreateUploadImageUrlValues) => {
    const requestUrl = '/medias/image-upload-url'
    const response = await http.post<MediaImageUploadUrlResponse>(requestUrl, data)
    return response.data
  }

  const createUploadVideoUrl = async (data: CreateUploadVideoUrlValues) => {
    const requestUrl = '/medias/video-upload-url'
    const response = await http.post<MediaVideoUploadUrlResponse>(requestUrl, data)
    return response.data
  }

  const uploadImage = async (file: File, data: CreateUploadImageUrlValues): Promise<UploadImageResponse> => {
    // 1. create upload url
    const createUploadUrlResponse = await createUploadImageUrl(data)

    // 2. upload image by upload url
    await uploadImageByUploadUrl(createUploadUrlResponse.uploadUrl, file)

    // 3. return image url & media uid
    return {
      imageUrl: getPublicCloudflareImageUrl(createUploadUrlResponse.mediaUid),
      mediaUid: createUploadUrlResponse.mediaUid,
    }
  }

  const uploadVideo = async (file: File, data: CreateUploadVideoUrlValues): Promise<UploadVideoResponse> => {
    // 1. create upload url
    const createUploadUrlResponse = await createUploadVideoUrl(data)

    // 2. upload video by upload url
    await uploadVideoByUploadUrl(createUploadUrlResponse.uploadUrl, file)

    // 3. return video url & media uid
    return {
      videoUrl: getPublicCloudflareVideoHlsUrl(createUploadUrlResponse.mediaUid),
      thumbnailUrl: getPublicCloudflareVideoThumbnailUrl(createUploadUrlResponse.mediaUid),
      mediaUid: createUploadUrlResponse.mediaUid,
    }
  }

  return {
    uploadImage,
    uploadVideo,
  }
}
